/**
 * A-06 完了（発注完了シート）＝06 §6.1 手順5・FR-127/FR-128（D-83）／**M1.9b＝シート化（D-101）**。
 *
 * ★Design（order-app.js の sheetDone）と同じ構成：**緑の丸に白いチェック** → 「発注が完了しました」
 *   → 説明 → **dt-row の表**（卸ごとの受付番号／送付先／お届け／合計（税込））→
 *   CTA「注文履歴で進捗を見る」＋「買い物を続ける」。
 * ★「発注が**完了**」＝送信の完了のこと。**確定とは言わない**（LC-02＝確定させるのは卸の人。
 *   すぐ下で「各卸の確認後、確定のお知らせが届きます」と言う）。
 * ★中身は**確定した送信の応答（CheckoutResult）**から作る＝シートの寿命の中だけの表示なので
 *   親をわざわざ引き直さない（リロードの概念が無い＝旧 /done ルートの控え取得は不要になった）。
 * ★送付先・お届けは**確定時のフォーム値の控え**（DoneMeta）＝親の API は現場・希望日を持たない
 *   （D-85）。卸ごとに値が割れたときは出さない（間違った1つを言い切らない）。
 * ★このシートだけ**背景タップで閉じない**（dismissable=false・Design と同じ）＝番号を控える前に
 *   うっかり閉じる事故を作らない。抜け道はボタン2つ。
 */

import type { CheckoutResult } from "@kenzai/api-client";
import { formatDate, Money } from "@kenzai/ui";

import { taxOf } from "./Cart";
import type { DoneMeta } from "../cartUi";

export function DoneSheetBody({
  result,
  meta,
  onGoOrders,
  onClose,
}: {
  result: CheckoutResult;
  meta: DoneMeta;
  /** 「注文履歴で進捗を見る」＝シートを閉じて /orders へ（配線は CartSheets） */
  onGoOrders: () => void;
  /** 「買い物を続ける」＝シートを閉じるだけ */
  onClose: () => void;
}) {
  const rows = result.orders;
  const single = rows.length === 1 ? rows[0] : null;
  const subtotal = result.total_amount === null ? null : Number(result.total_amount);

  return (
    <div style={{ textAlign: "center", paddingTop: "var(--kz-space-2)" }}>
      {/* 受け付けた瞬間だけ、印が小さく出てから定位置に収まる（M1.7 kz-pop-in・reduced-motion で停止） */}
      <span className="done-mark kz-pop-in" aria-hidden>
        <svg
          width="26"
          height="26"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.4}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="4 12 9 17 20 6" />
        </svg>
      </span>
      <div className="done-t" data-testid="done-title">
        発注が完了しました
      </div>
      <p className="done-s">
        {rows.length > 1 && `発注先ごとに${rows.length}件の注文に分かれました。`}
        各お取引先の確認後、確定のお知らせが届きます。
      </p>

      <div className="done-tbl">
        {rows.length > 1 && (
          // 複数社＝まとめ番号（CS-）が主役。お問い合わせは卸ごとの受付番号で（卸は親を知らない＝D-85）
          <div className="dt-row">
            <span>まとめ番号</span>
            <b className="kz-num" data-testid="done-checkout-no">
              {result.display_no}
            </b>
          </div>
        )}
        {rows.map((r) => (
          <div className="dt-row" key={r.tenant_id} data-testid={`done-child-${r.tenant_id}`}>
            <span>{r.tenant_name}</span>
            <b className="kz-num" data-testid={single !== null ? "done-no" : undefined}>
              {r.display_no}
            </b>
          </div>
        ))}
        {single !== null && (
          // 1社のときも親は在る（控え）＝小さく出す（問い合わせは受付番号で通じる）
          <div className="dt-row">
            <span>まとめ番号</span>
            <b className="kz-num" data-testid="done-checkout-no" style={{ fontWeight: 500, color: "var(--kz-color-ink-muted)" }}>
              {result.display_no}
            </b>
          </div>
        )}
        {meta.shipLabel !== null && (
          <div className="dt-row">
            <span>送付先</span>
            <b>{meta.shipLabel}</b>
          </div>
        )}
        {meta.date !== null && (
          <div className="dt-row">
            <span>お届け</span>
            <b>{formatDate(meta.date)}</b>
          </div>
        )}
        <div className="dt-row">
          <span>合計（税込）</span>
          <b className="kz-num">
            <Money value={subtotal === null ? null : String(subtotal + taxOf(subtotal))} />
          </b>
        </div>
      </div>

      <button
        type="button"
        onClick={onGoOrders}
        data-testid="go-progress"
        className="kz-btn kz-btn--accent kz-pressable"
        style={{ width: "100%", minHeight: 52, marginTop: "var(--kz-space-3)" }}
      >
        注文履歴で進捗を見る
      </button>
      <button
        type="button"
        onClick={onClose}
        className="kz-btn kz-btn--flat kz-pressable"
        style={{ width: "100%", minHeight: 44, marginTop: "var(--kz-space-2)" }}
      >
        買い物を続ける
      </button>
    </div>
  );
}
